import { useState, type FormEvent } from 'react';

import {
    Button,
    Drawer,
    DrawerFooter,
    DrawerHeader,
    Dropdown,
    Field,
    Input,
    Textarea,
} from '@/design-system';
import { NHG_CLASSIFICATION_LABELS, parseEuroToCents } from '@/entities/finding';
import {
    REPORT_COST_KEYS,
    REPORT_NHG_CLASSIFICATIONS,
    type ReportCostKey,
    type ReportFinding,
    type ReportFindingAdjustmentInput,
    type ReportNhgClassification,
} from '@/entities/report';
import { errorMessage, isApiError } from '@/shared/api';
import { formatCurrency, formatNumber } from '@/shared/lib';

import { reportCopy } from '../copy';

export interface AdjustFindingDrawerProps {
    /** De bevinding die wordt aangepast; `null` houdt het paneel dicht. */
    finding: ReportFinding | null;
    /** De vaste regels van de NHG-bijlage, voor de keuze van het onderdeel. */
    nhgRows: ReadonlyArray<{ code: string; element: string }>;
    /** Wie niet de inspecteur is, zet een aanpassing klaar die op akkoord wacht. */
    awaitsInspector: boolean;
    onClose: () => void;
    onSubmit: (finding: ReportFinding, input: ReportFindingAdjustmentInput) => Promise<unknown>;
}

const AMOUNT_LABELS: Record<ReportCostKey, string> = {
    direct: reportCopy.adjust.direct,
    termijn: reportCopy.adjust.term,
    onderhoud: reportCopy.adjust.improvement,
};

/** De velden van het formulier onder de namen die de API in een 422 gebruikt. */
const API_FIELDS = {
    direct: 'direct',
    termijn: 'termijn',
    onderhoud: 'onderhoud',
    nhgClassification: 'nhg_classification',
    nhgCode: 'nhg_code',
    reason: 'reason',
} as const;

/** "1.450" of "1.450,50": zoals je het intikt, zonder euroteken. */
function amountText(amount: number | null): string {
    if (amount === null) {
        return '';
    }

    return formatNumber(amount, Number.isInteger(amount) ? 0 : 2);
}

/**
 * "Bedragen aanpassen": de drie bedragen, de NHG-keuze, het NHG-onderdeel en een
 * verplichte reden van één bevinding in een concept.
 *
 * Het formulier begint bij wat er met de hand op staat; een leeg bedrag is weer het
 * bedrag uit de vragenlijst (bij een handmatige bevinding € 0). Het werk van het
 * paneel stopt bij het verzoek: het rapportscherm zet het opnieuw opgebouwde
 * concept in beeld.
 */
export function AdjustFindingDrawer({
    finding,
    nhgRows,
    awaitsInspector,
    onClose,
    onSubmit,
}: AdjustFindingDrawerProps) {
    const copy = reportCopy.adjust;

    return (
        <Drawer
            open={finding !== null}
            onClose={onClose}
            side="right"
            width={480}
            title={copy.title}
        >
            {finding ? (
                <>
                    <DrawerHeader
                        icon="edit"
                        eyebrow={finding.section}
                        title={copy.title}
                        description={finding.finding}
                        onClose={onClose}
                        closeLabel={copy.close}
                    />
                    {/* Een dichte Drawer rendert niets: elk openen begint bij de bevinding zoals ze nu is. */}
                    <AdjustFindingForm
                        key={finding.key}
                        finding={finding}
                        nhgRows={nhgRows}
                        awaitsInspector={awaitsInspector}
                        onCancel={onClose}
                        onSubmit={onSubmit}
                    />
                </>
            ) : null}
        </Drawer>
    );
}

interface FormValues {
    amounts: Record<ReportCostKey, string>;
    nhgClassification: ReportNhgClassification | '';
    nhgCode: string;
    reason: string;
}

function AdjustFindingForm({
    finding,
    nhgRows,
    awaitsInspector,
    onCancel,
    onSubmit,
}: {
    finding: ReportFinding;
    nhgRows: ReadonlyArray<{ code: string; element: string }>;
    awaitsInspector: boolean;
    onCancel: () => void;
    onSubmit: AdjustFindingDrawerProps['onSubmit'];
}) {
    const copy = reportCopy.adjust;
    const [values, setValues] = useState<FormValues>(() => ({
        amounts: {
            direct: amountText(finding.ownAmounts?.direct ?? null),
            termijn: amountText(finding.ownAmounts?.termijn ?? null),
            onderhoud: amountText(finding.ownAmounts?.onderhoud ?? null),
        },
        nhgClassification: finding.nhgClassification ?? '',
        nhgCode: finding.ownNhgCode ?? '',
        reason: '',
    }));
    const [invalid, setInvalid] = useState<Partial<Record<keyof typeof API_FIELDS, string>>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const fieldError = (field: keyof typeof API_FIELDS): string | undefined =>
        invalid[field] ??
        (isApiError(error) ? error.firstValidationError(API_FIELDS[field]) : undefined);

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const problems: Partial<Record<keyof typeof API_FIELDS, string>> = {};
        const amounts = {} as Record<ReportCostKey, number | null>;

        for (const key of REPORT_COST_KEYS) {
            const text = values.amounts[key].trim();
            const cents = text === '' ? null : parseEuroToCents(text);

            if (text !== '' && (cents === null || cents < 0)) {
                problems[key] = copy.amountInvalid;
            }

            amounts[key] = cents === null ? null : cents / 100;
        }

        if (values.reason.trim() === '') {
            problems.reason = copy.reasonMissing;
        }

        setInvalid(problems);

        if (Object.keys(problems).length > 0) {
            return;
        }

        setSaving(true);
        setError(null);

        try {
            await onSubmit(finding, {
                amounts,
                nhgClassification:
                    values.nhgClassification === '' ? null : values.nhgClassification,
                nhgCode: values.nhgCode === '' ? null : values.nhgCode,
                reason: values.reason.trim(),
            });
        } catch (caught) {
            setError(caught);
            setSaving(false);
        }
    };

    const generalProblem =
        error && !(isApiError(error) && error.kind === 'validation') ? errorMessage(error) : null;
    const templateCode = finding.ownNhgCode === null ? finding.nhgCode : undefined;

    return (
        <form
            onSubmit={(event) => void submit(event)}
            className="flex flex-1 flex-col pt-5"
            noValidate
        >
            <div className="mx-5 mb-6 grid gap-5">
                <fieldset className="m-0 grid gap-3 border-0 p-0">
                    <legend className="mb-1 p-0 font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
                        {copy.amountsTitle}
                    </legend>
                    <p className="m-0 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                        {finding.manual ? copy.amountsNoteManual : copy.amountsNote}
                    </p>
                    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(128px,1fr))]">
                        {REPORT_COST_KEYS.map((key) => (
                            <Field
                                key={key}
                                label={AMOUNT_LABELS[key]}
                                description={
                                    finding.calculatedCosts
                                        ? copy.fromQuestionnaire(
                                              formatCurrency(finding.calculatedCosts[key]),
                                          )
                                        : undefined
                                }
                                error={fieldError(key)}
                            >
                                <Input
                                    mono
                                    inputMode="decimal"
                                    autoComplete="off"
                                    placeholder={
                                        finding.calculatedCosts
                                            ? amountText(finding.calculatedCosts[key])
                                            : '0'
                                    }
                                    value={values.amounts[key]}
                                    onChange={(event) => {
                                        const next = event.target.value;

                                        setValues((current) => ({
                                            ...current,
                                            amounts: { ...current.amounts, [key]: next },
                                        }));
                                    }}
                                />
                            </Field>
                        ))}
                    </div>
                </fieldset>

                <Field label={copy.nhgChoice} error={fieldError('nhgClassification')}>
                    <Dropdown
                        value={values.nhgClassification}
                        options={[
                            { value: '' as const, label: copy.nhgChoiceDefault },
                            ...REPORT_NHG_CLASSIFICATIONS.map((option) => ({
                                value: option,
                                label: NHG_CLASSIFICATION_LABELS[option],
                            })),
                        ]}
                        onChange={(next) =>
                            setValues((current) => ({ ...current, nhgClassification: next }))
                        }
                    />
                </Field>

                <Field label={copy.nhgCode} error={fieldError('nhgCode')}>
                    <Dropdown
                        value={values.nhgCode}
                        options={[
                            { value: '', label: copy.nhgCodeDefault(templateCode) },
                            ...nhgRows.map((row) => ({
                                value: row.code,
                                label: `${row.code} ${row.element}`,
                            })),
                        ]}
                        onChange={(next) => setValues((current) => ({ ...current, nhgCode: next }))}
                    />
                </Field>

                <Field label={copy.reason} error={fieldError('reason')}>
                    <Textarea
                        rows={3}
                        maxLength={500}
                        placeholder={copy.reasonPlaceholder}
                        value={values.reason}
                        onChange={(event) => {
                            const next = event.target.value;

                            setValues((current) => ({ ...current, reason: next }));
                        }}
                    />
                </Field>

                {awaitsInspector ? (
                    <p className="m-0 rounded-[8px] border border-[var(--warn-border)] bg-[var(--warn-tint)] px-3 py-[10px] text-[13px] leading-[1.5] text-[var(--warn-text)]">
                        {copy.awaitsNote}
                    </p>
                ) : null}

                {generalProblem ? (
                    <p role="alert" className="m-0 text-[13px] leading-[1.5] text-[var(--danger)]">
                        {generalProblem}
                    </p>
                ) : null}
            </div>

            <DrawerFooter>
                <Button type="submit" variant="primary" size="md" disabled={saving}>
                    {saving ? copy.saving : copy.save}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="md"
                    disabled={saving}
                    onClick={onCancel}
                >
                    {copy.cancel}
                </Button>
            </DrawerFooter>
        </form>
    );
}
